
import React, { useEffect, useRef, useState } from 'react';
import { Loader } from '@googlemaps/js-api-loader';
import { calculateGeoblocBoundaries } from '@/utils/geoblocBoundaries';
import { getCurrentLocation } from '@/utils/locationUtils';
import { getGoogleMapsClientConfig } from '@/utils/googleMapsConfig';
import ShowDirectionDialog from '@/components/search/ShowDirectionDialog';
interface GoogleMapProps {
  latitude: number;
  longitude: number;
  plusCode?: string;
  address?: string;
  className?: string;
  showGeoblocBoundary?: boolean;
  globalPlusCode?: string;
  googleMapsSearchCode?: string;
  buildingNumber?: string;
  /** In-app WebView: same hybrid map, one-finger pan, no site chrome. */
  embedded?: boolean;
  /** Keep plus codes internal; do not put them in the info window. */
  hidePlusCode?: boolean;
}

const GoogleMap: React.FC<GoogleMapProps> = ({ 
  latitude, 
  longitude, 
  plusCode, 
  address,
  className = "w-full h-64",
  showGeoblocBoundary = false,
  globalPlusCode,
  googleMapsSearchCode,
  buildingNumber,
  embedded = false,
  hidePlusCode = false,
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const [isContainerReady, setIsContainerReady] = React.useState(false);
  const [routeOpen, setRouteOpen] = useState(false);
  const [routeOrigin, setRouteOrigin] = useState<{ lat: number; lng: number } | null>(null);
  // The map's DOM buttons call this ref so they do not close over a stale opener.
  const startRouteRef = useRef<() => void>(() => {});

  // Use ResizeObserver to detect when the map container is actually visible and sized
  useEffect(() => {
    if (!mapRef.current) return;
    
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // Only mark as ready when the container has actual dimensions
        if (entry.contentRect.width > 0 && entry.contentRect.height > 0) {
          setIsContainerReady(true);
        }
      }
    });
    
    observer.observe(mapRef.current);
    
    // Also check immediately in case it's already visible
    if (mapRef.current.offsetWidth > 0 && mapRef.current.offsetHeight > 0) {
      setIsContainerReady(true);
    }
    
    return () => observer.disconnect();
  }, []);

  // Start Navigate used to window.open a Maps URL. Capacitor sends that to Safari or Chrome.
  const startInAppNavigation = async () => {
    try {
      const location = await getCurrentLocation();
      setRouteOrigin(location);
    } catch {
      setRouteOrigin(null);
    }
    setRouteOpen(true);
  };
  startRouteRef.current = () => {
    void startInAppNavigation();
  };

  useEffect(() => {
    // Don't initialize until container is ready with proper dimensions
    if (!isContainerReady || !mapRef.current) return;
    
    const initializeMap = async () => {
      try {
        const { apiKey, mapId } = await getGoogleMapsClientConfig();

        // Detect user's region for optimized map tile serving and localization
        // Geolocation for region bias omitted — must only be requested on user gesture (browser policy)
        let regionCode: string | undefined;

        const loaderConfig: any = {
          apiKey: apiKey,
          version: 'weekly',
          libraries: ['places', 'marker']
        };
        
        // Add region parameter if detected for better CDN routing and localization
        if (regionCode) {
          loaderConfig.region = regionCode;
        }

        const loader = new Loader(loaderConfig);

        await loader.load();

        if (!mapRef.current) {
          console.warn('Map container not available after loader');
          return;
        }

        // Clear any previous map instance
        if (mapInstanceRef.current) {
          mapInstanceRef.current = null;
        }

        // Enhanced: Modern Google Maps styling that matches the web version
        const mapOptions: google.maps.MapOptions = {
          center: { lat: latitude, lng: longitude },
          zoom: 18,
          mapTypeId: google.maps.MapTypeId.HYBRID, // Use hybrid for satellite + roads
          tilt: 0, // Modern flat view
          gestureHandling: embedded ? 'greedy' : 'cooperative',
          zoomControl: true,
          mapTypeControl: true,
          scaleControl: !embedded,
          streetViewControl: !embedded,
          rotateControl: false,
          fullscreenControl: !embedded,
          // Note: styles are controlled via Cloud Console when mapId is present
        };

        if (mapId) {
          mapOptions.mapId = mapId;
        }

        const map = new google.maps.Map(mapRef.current, mapOptions);

        // Expose a global navigate handler for InfoWindows
        // Info-window navigation stays on this map's in-app route.
        (window as any).__qlsNavigate = () => {
          startRouteRef.current();
        };
        const displayPlusCode = plusCode || globalPlusCode;
        const searchablePlusCode = googleMapsSearchCode || displayPlusCode;
        
        const markerTitle = hidePlusCode
          ? (buildingNumber ? `Building ${buildingNumber}` : address || 'QLS Location')
          : buildingNumber
            ? `Building ${buildingNumber} (${displayPlusCode})`
            : address || searchablePlusCode || 'QLS Location';

        // Use AdvancedMarkerElement only when a real map ID is configured.
        const markerElement = document.createElement('div');
        const outerDiv = document.createElement('div');
        outerDiv.style.cssText = 'width: 32px; height: 32px; background: #EA4335; border: 3px solid white; border-radius: 50%; box-shadow: 0 2px 8px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center; position: relative;';
        const innerDiv = document.createElement('div');
        innerDiv.style.cssText = 'width: 8px; height: 8px; background: white; border-radius: 50%;';
        outerDiv.appendChild(innerDiv);
        markerElement.appendChild(outerDiv);

        const marker = mapId
          ? new google.maps.marker.AdvancedMarkerElement({
              position: { lat: latitude, lng: longitude },
              map,
              title: markerTitle,
              content: markerElement,
            })
          : new google.maps.Marker({
              position: { lat: latitude, lng: longitude },
              map,
              title: markerTitle,
            });

        // Build info window content using DOM API to prevent XSS
        const infoContainer = document.createElement('div');
        infoContainer.style.cssText = 'max-width: 380px; font-family: Google Sans, Roboto, Arial, sans-serif;';

        if (buildingNumber) {
          const h3 = document.createElement('h3');
          h3.style.cssText = 'font-weight: 500; margin: 0 0 8px 0; color: #202124; font-size: 16px;';
          h3.textContent = buildingNumber;
          infoContainer.appendChild(h3);
          const p = document.createElement('p');
          p.style.cssText = 'margin: 0 0 8px 0; color: #5f6368; font-size: 14px;';
          p.textContent = address || 'QLS Location';
          infoContainer.appendChild(p);
        } else {
          const h3 = document.createElement('h3');
          h3.style.cssText = 'font-weight: 500; margin: 0 0 8px 0; color: #202124; font-size: 16px;';
          h3.textContent = address || 'QLS Location';
          infoContainer.appendChild(h3);
        }

        if (!hidePlusCode && googleMapsSearchCode) {
          const codeBox = document.createElement('div');
          codeBox.style.cssText = 'margin: 8px 0; padding: 12px; background: #e8f0fe; border-radius: 8px;';
          const codeLabel = document.createElement('p');
          codeLabel.style.cssText = 'margin: 0; font-weight: 500; color: #1a73e8; font-size: 13px;';
          codeLabel.textContent = '📍 Searchable in Google Maps';
          const codeValue = document.createElement('p');
          codeValue.style.cssText = 'margin: 4px 0 0 0; font-family: Google Sans Mono, monospace; font-size: 14px; color: #1967d2;';
          codeValue.textContent = googleMapsSearchCode;
          codeBox.appendChild(codeLabel);
          codeBox.appendChild(codeValue);
          infoContainer.appendChild(codeBox);
        }

        if (!hidePlusCode && displayPlusCode) {
          const plusP = document.createElement('p');
          plusP.style.cssText = 'margin: 6px 0; color: #5f6368; font-size: 14px;';
          plusP.innerHTML = '<strong>Plus Code:</strong> ';
          const codeSpan = document.createElement('span');
          codeSpan.style.fontFamily = 'Google Sans Mono, monospace';
          codeSpan.textContent = displayPlusCode;
          plusP.appendChild(codeSpan);
          infoContainer.appendChild(plusP);
        }

        const coordP = document.createElement('p');
        coordP.style.cssText = 'margin: 6px 0; color: #5f6368; font-size: 14px;';
        coordP.innerHTML = '<strong>Coordinates:</strong> ';
        coordP.appendChild(document.createTextNode(`${latitude.toFixed(6)}, ${longitude.toFixed(6)}`));
        infoContainer.appendChild(coordP);

        const btnRow = document.createElement('div');
        btnRow.style.cssText = 'margin: 16px 0 0 0; display: flex; gap: 8px;';
        const navBtn = document.createElement('button');
        navBtn.style.cssText = 'background: #1a73e8; color: white; border: none; padding: 10px 16px; border-radius: 24px; cursor: pointer; font-size: 14px; font-weight: 500; font-family: Google Sans, Roboto, Arial, sans-serif; display: flex; align-items: center; gap: 6px;';
        navBtn.textContent = '🚗 Start Navigate';
        navBtn.addEventListener('click', () => {
          startRouteRef.current();
        });
        btnRow.appendChild(navBtn);
        infoContainer.appendChild(btnRow);

        const tipBox = document.createElement('div');
        tipBox.style.cssText = 'margin: 12px 0 0 0; padding: 8px 12px; background: #e6f4ea; border-radius: 8px;';
        const tipP = document.createElement('p');
        tipP.style.cssText = 'margin: 0; font-size: 12px; color: #137333; font-weight: 400;';
        tipP.innerHTML = '💡 <strong>Tip:</strong> ';
        tipP.appendChild(document.createTextNode('Tap Start Navigate to see the route in this app'));
        tipBox.appendChild(tipP);
        infoContainer.appendChild(tipBox);

        const infoWindow = new google.maps.InfoWindow({
          content: infoContainer,
          maxWidth: 400
        });

        marker.addListener('click', () => {
          if (mapId) {
            infoWindow.open({ map, anchor: marker as google.maps.marker.AdvancedMarkerElement });
          } else {
            infoWindow.open(map, marker as google.maps.Marker);
          }
        });

        marker.addListener('dblclick', () => {
          startRouteRef.current();
        });

        // Add geobloc boundary if requested
        if (showGeoblocBoundary && displayPlusCode) {
          const boundary = calculateGeoblocBoundaries(displayPlusCode, { latitude, longitude });
          
          console.log('Creating geobloc boundary rectangle:', boundary);
          
          const rectangle = new google.maps.Rectangle({
            bounds: {
              north: boundary.northEast.lat,
              south: boundary.southWest.lat,
              east: boundary.northEast.lng,
              west: boundary.southWest.lng,
            },
            fillColor: 'transparent',
            fillOpacity: 0,
            strokeColor: '#ea4335',
            strokeWeight: 2,
            strokeOpacity: 0.8,
            strokePosition: google.maps.StrokePosition.OUTSIDE,
            map: map,
          });

          rectangle.addListener('click', () => {
            // Build geobloc info window using DOM API to prevent XSS
            const geoblocContainer = document.createElement('div');
            geoblocContainer.style.fontFamily = "'Google Sans', Roboto, Arial, sans-serif";

            const geoblocH4 = document.createElement('h4');
            geoblocH4.style.cssText = 'font-weight: 500; color: #ea4335; margin: 0 0 8px 0; font-size: 14px;';
            geoblocH4.textContent = 'Geobloc Boundary';
            geoblocContainer.appendChild(geoblocH4);

            const geoblocIdP = document.createElement('p');
            geoblocIdP.style.cssText = 'margin: 4px 0; font-size: 13px; color: #5f6368;';
            const geoblocStrong = document.createElement('strong');
            geoblocStrong.textContent = 'Geobloc ID: ';
            geoblocIdP.appendChild(geoblocStrong);
            geoblocIdP.appendChild(document.createTextNode(boundary.geobloc));
            geoblocContainer.appendChild(geoblocIdP);

            const radiusP = document.createElement('p');
            radiusP.style.cssText = 'margin: 4px 0; font-size: 13px; color: #5f6368;';
            const radiusStrong = document.createElement('strong');
            radiusStrong.textContent = 'Radius: ';
            radiusP.appendChild(radiusStrong);
            radiusP.appendChild(document.createTextNode(`~${boundary.approximateRadius}m`));
            geoblocContainer.appendChild(radiusP);

            if (buildingNumber) {
              const buildingP = document.createElement('p');
              buildingP.style.cssText = 'margin: 4px 0; font-size: 13px; color: #5f6368;';
              const buildingStrong = document.createElement('strong');
              buildingStrong.textContent = 'Building: ';
              buildingP.appendChild(buildingStrong);
              buildingP.appendChild(document.createTextNode(buildingNumber));
              geoblocContainer.appendChild(buildingP);
            }

            const noteP = document.createElement('p');
            noteP.style.cssText = 'margin: 8px 0 0 0; font-size: 11px; color: #80868b;';
            noteP.textContent = 'Optimized for walking paths and precise delivery';
            geoblocContainer.appendChild(noteP);

            const geoblocInfoWindow = new google.maps.InfoWindow({
              content: geoblocContainer,
              position: { lat: boundary.centerLat, lng: boundary.centerLng }
            });
            
            geoblocInfoWindow.open(map);
          });

          console.log('Enhanced geobloc boundary with building number context added');
        }

        mapInstanceRef.current = map;

      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      }
    };

    initializeMap();

    return () => {
      if ((window as any).__qlsNavigate) {
        delete (window as any).__qlsNavigate;
      }
      if (mapInstanceRef.current) {
        mapInstanceRef.current = null;
      }
    };
  }, [isContainerReady, latitude, longitude, plusCode, address, showGeoblocBoundary, globalPlusCode, googleMapsSearchCode, buildingNumber, embedded, hidePlusCode]);

  return (
    <div className={className}>
      <div ref={mapRef} className={embedded ? 'w-full h-full' : 'w-full h-full rounded-lg shadow-sm border border-gray-200'} />
      <ShowDirectionDialog
        open={routeOpen}
        onOpenChange={setRouteOpen}
        origin={routeOrigin}
        destination={{ lat: latitude, lng: longitude }}
        mode="driving"
      />
    </div>
  );
};

export default GoogleMap;
